import React from 'react';
import { InvShell, InvStageChip } from './InvShell';
import type { InvTabId } from './InvShell';
import {
  INV_LINE_CONVERSATIONS,
  INV_LINE_PASSED,
  INV_LINE_QUESTIONS,
  INV_LINE_UNANSWERED,
  INV_NUMBERS,
} from '../../invisalign/mockData';

export interface InvLineViewProps {
  onSelectTab?: (tab: InvTabId) => void;
  onOpenSettings?: () => void;
  onHold: boolean;
  onSetHold?: (next: boolean) => void;
  onOpenPatient?: (id: string) => void;
  /** Opens Train ELVA with the question pre-filled (amendment 34 §3.4). */
  onAnswerQuestion?: ((question: string) => void) | undefined;
  /** Opens the Communication log filtered to the Invis-Line. */
  onOpenCommLog?: (() => void) | undefined;
}

export function InvLineView(props: InvLineViewProps) {
  const { onSelectTab, onOpenSettings, onHold, onSetHold, onOpenPatient, onAnswerQuestion, onOpenCommLog } = props;
  const n = INV_NUMBERS;
  const max = Math.max(...INV_LINE_QUESTIONS.map((q) => q.count));

  return (
    <InvShell active="line" onSelectTab={onSelectTab} onOpenSettings={onOpenSettings} onHold={onHold} onSetHold={onSetHold}>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">The Invis-Line</span>
        <span className="rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2.5 py-0.5 font-[var(--font-mono)] text-[12px] text-[var(--text-secondary)]">{n.lineNumber}</span>
        <span className="text-[12px] text-[var(--text-muted)]">Last 30 days · Answers texts and calls patients start, and sends up to 3 programme texts to patients who said yes. Never calls out.</span>
        <button type="button" className="ml-auto text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenCommLog}>All {n.lineConversations} →</button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2.5">
        <Stat v={n.lineConversations} l="conversations" />
        <Stat v={n.lineTexts} l="by text" />
        <Stat v={n.lineCalls} l="by call" />
        <Stat v={n.linePatients} l="patients" />
        <Stat v={n.lineBecameReady} l="became ready" />
        <Stat v={n.lineBooked} l="booked" />
      </div>

      <div className="mt-4 grid grid-cols-[1.1fr_1fr] gap-4 max-[900px]:grid-cols-1">
        {/* what they asked */}
        <Card>
          <H>What they asked</H>
          <div className="flex flex-col gap-2">
            {INV_LINE_QUESTIONS.map((q) => (
              <div key={q.label} className="flex items-center gap-2">
                <span className="w-[150px] shrink-0 text-[12px] text-[var(--text-secondary)]">{q.label}</span>
                <span className="h-3 rounded-full bg-[var(--brand-indigo)]" style={{ width: `${String((q.count / max) * 200)}px`, minWidth: 8 }} />
                <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-primary)]">{q.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* couldn't answer + passed on */}
        <div className="flex flex-col gap-4">
          <Card>
            <H>Couldn’t answer — {n.lineUnanswered}</H>
            <div className="flex flex-col gap-2">
              {INV_LINE_UNANSWERED.map((u) => (
                <div key={u.question} className="rounded-[10px] bg-[var(--warn-amber-fill)] px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[12.5px] text-[var(--text-primary)]">{u.question}</div>
                    <button type="button" className="shrink-0 text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { onAnswerQuestion?.(u.question); }}>Answer it once →</button>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">{u.patients}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <H>Passed to a person</H>
            <div className="flex flex-col">
              {INV_LINE_PASSED.map((p) => (
                <div key={p.text} className="flex items-baseline justify-between gap-3 border-t border-[var(--border-default)] py-2 first:border-0 text-[12px]">
                  <span className="text-[var(--text-secondary)]">{p.text}</span>
                  <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{p.date}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* recent conversations */}
      <Card className="mt-4">
        <H>Recent conversations</H>
        <div className="flex flex-col gap-3">
          {INV_LINE_CONVERSATIONS.map((c) => (
            <div key={c.id} className="rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="font-semibold text-[var(--text-primary)] hover:underline" onClick={() => { onOpenPatient?.(c.id); }}>{c.name}</button>
                <InvStageChip stage={c.stage} />
                <span className="text-[11px] text-[var(--text-muted)]">{c.channel === 'text' ? 'Text' : 'Call'} · {c.when}</span>
              </div>
              {c.programmeText !== undefined ? (
                <div className="mt-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] p-2.5">
                  <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--brand-indigo)]">Programme text · day {c.programmeText.day}</div>
                  <div className="mt-0.5 text-[12px] leading-[1.5] text-[var(--text-secondary)]">{c.programmeText.body}</div>
                </div>
              ) : null}
              <div className="mt-2 text-[12.5px] text-[var(--text-primary)]"><span className="font-semibold text-[var(--text-muted)]">Q · </span>{c.q}</div>
              <div className="mt-1 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]"><span className="font-semibold text-[var(--brand-indigo)]">ELVA · </span>{c.a}</div>
              {c.patientReply !== undefined ? <div className="mt-1 text-[12px] italic text-[var(--text-muted)]">↳ {c.patientReply}</div> : null}
            </div>
          ))}
        </div>
      </Card>
    </InvShell>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)] ${className ?? ''}`}>{children}</div>;
}
function H({ children }: { children: React.ReactNode }) {
  return <h2 className="m-0 mb-3 text-[13px] font-bold text-[var(--text-primary)]">{children}</h2>;
}
function Stat({ v, l }: { v: number; l: string }) {
  return (
    <div className="rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3.5 py-2 shadow-[var(--shadow-card)]">
      <div className="font-[var(--font-mono)] text-[20px] font-bold text-[var(--text-primary)]">{v}</div>
      <div className="text-[11px] text-[var(--text-muted)]">{l}</div>
    </div>
  );
}
